import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import {
  Chip,
  IconButton,
  InputAdornment,
  InputBaseComponentProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import clsx from 'clsx';
import { FC, forwardRef, useEffect, useRef, useState } from 'react';
import { v4 as genUuid } from 'uuid';
import { AutocompleteItem, AutocompleteList } from '../AutocompleteList';
import { useDebounce } from '../utils';
import { useStyles } from './styles';

const CustomInput: FC<InputBaseComponentProps> = forwardRef<
  HTMLDivElement,
  InputBaseComponentProps
>(
  (
    {
      inputRef,
      disabled,
      classes,
      variant,
      cColor,
      cValue,
      cOnChange,
      autocompleteList,
      loading,
      error,
      maxLength,
      cOnChipClick,
      ...inputProps
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(
          classes.inputWrapper,
          variant === 'outlined' && classes.inputWrapperOutlined,
          variant === 'filled' && classes.inputWrapperFilled,
        )}
      >
        {cValue?.map((item: AutocompleteItem, index: number) => {
          return (
            <Chip
              key={item.id}
              color={item.color ? 'primary' : cColor}
              style={{ ...(item.color ? { backgroundColor: item.color } : {}) }}
              label={item.label}
              onClick={cOnChipClick && (() => cOnChipClick(item, index))}
              onDelete={
                cOnChange &&
                (() =>
                  cOnChange(
                    cValue.filter(({ id }: { id: string }) => id !== item.id),
                  ))
              }
              className={classes.chip}
              disabled={disabled || item.disabled}
            />
          );
        })}
        {(!maxLength || !cValue || cValue.length < maxLength) && (
          <input
            {...inputProps}
            ref={inputRef}
            disabled={disabled}
            className={clsx(
              classes.chipsInput,
              variant === 'outlined' && classes.chipsInputOutlined,
              variant === 'filled' && classes.chipsInputFilled,
            )}
          />
        )}
      </div>
    );
  },
);

export interface ChipsInputProps {
  value?: AutocompleteItem[];
  onChange?: (value: AutocompleteItem[]) => void;
  inputRef?: any;
  label: string;
  className?: string;
  color?: 'primary' | 'secondary';
  disabled?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  maxLength?: number;
  onChipClick?: (item: AutocompleteItem, index: number) => void;
  autocomplete?: {
    fetchList: (search: string) => Promise<AutocompleteItem[] | undefined>;
    mandatoryInput?: boolean;
    selectChildren?: boolean;
  };
  TextFieldProps?: TextFieldProps;
  showLevelIcons?: boolean;
  startingIcon?: JSX.Element;
}

export const ChipsInput: FC<ChipsInputProps> = ({
  value = [],
  onChange,
  inputRef,
  label,
  className,
  color,
  disabled,
  variant = 'standard',
  autocomplete,
  maxLength,
  onChipClick,
  TextFieldProps: textFieldProps,
  showLevelIcons,
  startingIcon,
}) => {
  const classes = useStyles();
  const [textInput, setTextInput] = useState('');
  const [focus, setFocus] = useState(false);
  const labelShifted = !focus && !textInput && !value?.length;
  const [autocompleteList, setAutocompleteList] = useState<
    AutocompleteItem[] | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const debouncedInput = useDebounce(textInput, 500);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const displayList =
    !!autocomplete &&
    focus &&
    !disabled &&
    !!(!autocomplete.mandatoryInput || debouncedInput);

  useEffect(() => {
    if (displayList && autocomplete) {
      setError(undefined);
      setLoading(true);
      autocomplete
        .fetchList(debouncedInput)
        .then((res) => {
          setAutocompleteList(res);
        })
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [autocomplete, debouncedInput, displayList]);

  const newChipFromInput = () => {
    if (textInput && !value.find((item) => item.label === textInput)) {
      onChange &&
        onChange([...(value || []), { id: genUuid(), label: textInput }]);
    }
    setTextInput('');
  };

  const onSelect = (item: AutocompleteItem) => {
    if (value.find(({ id }) => id === item.id)) {
      onChange?.(value.filter(({ id }) => id !== item.id));
    } else {
      let itemsToAdd = [item];

      // selectChildren option (select all children of the selected item as well)
      if (autocomplete?.selectChildren && autocompleteList) {
        const itemIndex =
          autocompleteList?.findIndex(({ id }) => id === item.id) || 0;
        // eslint-disable-next-line fp/no-loops
        for (const elem of autocompleteList.slice(itemIndex + 1)) {
          if (elem?.level && elem.level > (item.level || 0)) {
            if (!value.find(({ id }) => id === elem?.id)) {
              itemsToAdd = [...itemsToAdd, elem];
            }
          } else {
            break;
          }
        }
      }

      onChange?.([...(value || []), ...itemsToAdd]);
    }

    if (textInput) {
      setTextInput('');
      setFocus(false);
    }
  };

  return (
    <>
      <div
        className={clsx(classes.wrapper, className)}
        ref={wrapperRef}
        // To prevent Megadraft focus bug
        onFocus={(e) => e.stopPropagation()}
      >
        <TextField
          className={classes.textField}
          variant={variant}
          label={label}
          disabled={disabled}
          placeholder={!labelShifted && value.length ? `Ajouter` : undefined}
          value={textInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && textInput && !autocomplete) {
              e.stopPropagation();
              newChipFromInput();
            }
          }}
          onChange={(e) => setTextInput(e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => {
            if (!autocomplete) {
              newChipFromInput();
            }
          }}
          helperText={
            textInput && focus && !autocomplete
              ? 'Appuyez sur Entrer pour ajouter'
              : undefined
          }
          {...textFieldProps}
          InputProps={{
            inputComponent: CustomInput,
            inputProps: {
              inputRef,
              classes,
              variant,
              cColor: color,
              cValue: value,
              cOnChange: onChange,
              autocompleteList,
              loading,
              error,
              maxLength,
              cOnChipClick: onChipClick,
            },
            ...textFieldProps?.InputProps,
            ...(startingIcon && {
              startAdornment: (
                <InputAdornment position="start">{startingIcon}</InputAdornment>
              ),
            }),
          }}
          InputLabelProps={{
            shrink: value.length ? true : undefined,
            ...textFieldProps?.InputLabelProps,
          }}
        />

        {autocomplete && !autocomplete.mandatoryInput && !textInput && (
          <IconButton
            classes={{
              root: clsx(
                variant === 'standard' && classes.arrowIconStandard,
                (variant === 'outlined' || variant === 'filled') &&
                  classes.arrowIconOutlined,
              ),
            }}
            data-testid="chips-icon-button"
            size="small"
            onClick={() => setFocus((prev) => !prev)}
            disabled={disabled}
          >
            {displayList ? <ArrowDropUp /> : <ArrowDropDown />}
          </IconButton>
        )}

        <AutocompleteList
          showLevelIcons={showLevelIcons}
          open={displayList}
          anchorRef={wrapperRef}
          list={autocompleteList?.map((item) => ({
            ...item,
            selected: !!value.find(({ id }) => id === item.id),
          }))}
          loading={loading}
          error={error}
          onSelect={onSelect}
          onClose={() => {
            setFocus(false);
            setTextInput('');
          }}
        />
      </div>
    </>
  );
};
